import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import {
  TokenExchangeService,
  SKIP_TOKEN_EXCHANGE,
} from '../../shared/services/token-exchange.service';
import { environment } from 'src/environments/environment';
import { LoggerService } from '../logger.service';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  loginInProgress = false;

  constructor(
    private msalService: MsalService,
    private router: Router,
    private tokenExchangeService: TokenExchangeService,
    private loggerService: LoggerService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for the token exchange request itself
    if (req.context.get(SKIP_TOKEN_EXCHANGE)) {
      return next.handle(req);
    }

    // If x-exclude-token header is set, skip auth
    if (req.headers.has('x-exclude-token')) {
      return next.handle(req);
    }

    // Skip auth for Azure Storage SAS URLs — they use query-string signatures
    // and will be rejected by Azure if an Authorization header is also present.
    const fullUrl = req.urlWithParams;
    const isSasUrl =
      fullUrl.includes('.windows.net') ||
      fullUrl.includes('.blob.core') ||
      fullUrl.includes('.file.core') ||
      (fullUrl.includes('sig=') && fullUrl.includes('sp=') && fullUrl.includes('sv='));
    if (isSasUrl) {
      return next.handle(req);
    }

    const requestId = req.headers.get('x-request-id') || '';
    const isProtectedApiRequest = this.isProtectedApiRequest(req.url);

    // Check if user has any MSAL accounts (i.e. has logged in)
    const hasAccounts = this.msalService.instance.getAllAccounts().length > 0;

    if (!hasAccounts) {
      this.logAuthIssue(req, 'No active MSAL account found', {
        requestId,
        isProtectedApiRequest,
      });
      this.forceLoginRedirect('No active MSAL account found');
      if (isProtectedApiRequest) {
        return throwError(() => new Error('Protected API request blocked due to missing MSAL account'));
      }
      return next.handle(req);
    }

    return this.tokenExchangeService.getValidProxyToken().pipe(
      switchMap((token) => {
        const authRequest = this.buildAuthorizedRequest(req, token);
        return next.handle(authRequest).pipe(
          catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
              this.logAuthIssue(req, 'Protected API returned 401; retrying with refreshed proxy token', {
                requestId,
                status: error.status,
                statusText: error.statusText,
                isProtectedApiRequest,
              });
              return this.tokenExchangeService.exchangeToken(true).pipe(
                switchMap((newToken) => {
                  const retryRequest = this.buildAuthorizedRequest(req, newToken);
                  return next.handle(retryRequest);
                }),
                catchError((err) => {
                  this.logAuthIssue(req, 'Token refresh retry failed after 401', {
                    requestId,
                    isProtectedApiRequest,
                    retryError: this.toSerializableError(err),
                  });
                  this.forceLoginRedirect('Token refresh retry failed after 401');
                  return throwError(
                    () => new Error('Failed to refresh proxy token after unauthorized response')
                  );
                })
              );
            }
            return throwError(() => error);
          })
        );
      }),
      catchError((error) => {
        this.logAuthIssue(req, 'Failed to obtain proxy token', {
          requestId,
          isProtectedApiRequest,
          error: this.toSerializableError(error),
        });
        this.forceLoginRedirect('Failed to obtain proxy token');
        if (isProtectedApiRequest) {
          // Never allow protected APIs to proceed without a proxy token.
          return throwError(() => new Error('Proxy token required for protected API but not available'));
        }
        // Non-protected calls can continue without auth.
        return next.handle(req);
      })
    );
  }

  private buildAuthorizedRequest(req: HttpRequest<any>, token: string): HttpRequest<any> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'x-optum-api-key': environment.config.apis.optumApiKey,
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
  }

  private forceLoginRedirect(reason: string): void {
    if (this.loginInProgress) {
      return;
    }

    this.loginInProgress = true;
    console.warn(`Forcing login redirect: ${reason}`);
    this.msalService.loginRedirect();
  }

  private isProtectedApiRequest(url: string): boolean {
    const protectedKeys = (environment.authConfig?.addedProtectedResourceMap || [])
      .map((x: any) => x?.key)
      .filter((x: string) => !!x);

    if (protectedKeys.some((key: string) => url.startsWith(key))) {
      return true;
    }

    // Fallback for environments where protected-resource map is incomplete.
    return url.includes('api.uhg.com/api') || url.includes('api-stg.uhg.com/api');
  }

  private logAuthIssue(req: HttpRequest<any>, message: string, details: any): void {
    const diagnostics = {
      ...details,
      tokenState: this.tokenExchangeService.getDiagnosticsSnapshot(),
      hasAuthorizationHeader: req.headers.has('Authorization'),
      method: req.method,
      urlWithParams: req.urlWithParams,
    };

    const errorData: Error = new Error(message);
    errorData['payload'] = req.body;
    errorData['url'] = req.url;
    errorData['apiError'] = diagnostics;
    this.loggerService.logException(errorData);
    console.error(`Auth interceptor: ${message}`, diagnostics);
  }

  private toSerializableError(error: any): any {
    if (!error) {
      return null;
    }

    if (error instanceof HttpErrorResponse) {
      return {
        status: error.status,
        statusText: error.statusText,
        message: error.message,
        url: error.url,
        error: error.error,
      };
    }

    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
}
